import { useIntl } from '@umijs/max';
import { Empty, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import UserDetailModal from '@/components/UserDetailModal';
import { useUserMap } from '@/hooks';
import {
  getLLMInFlight,
  type LLMInFlightResult,
  type LLMInFlightTask,
  type LLMWaitingTask,
} from '@/services/drama/api';
import { usePlainRealtime } from '@/services/drama/realtime';
import { sanitizeError } from '@/utils';
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';

const POLL_INTERVAL_MS = 3_000;
const POLL_BACKOFF_MAX_MS = 30_000;

const WS_FALLBACK_MS = 20_000;

const useStyles = createStyles(({ token }) => ({
  panel: {
    width: '100%',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 8,
    marginBottom: 8,
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
  },
  panelTitle: {
    fontSize: 13,
    fontWeight: 600,
  },
  panelMeta: {
    fontSize: 11,
    color: token.colorTextTertiary,
  },
  taskRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '8px 0',
    borderBottom: `1px solid ${token.colorBorderSecondary}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  taskRowHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  taskRowSub: {
    fontSize: 11,
    color: token.colorTextTertiary,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  taskNote: {
    fontSize: 11,
    color: token.colorTextSecondary,
    fontFamily: token.fontFamilyCode,
    wordBreak: 'break-all',
  },
  emptyWrap: {
    padding: '16px 0',
  },
  degradedBanner: {
    fontSize: 11,
    color: token.colorWarningText,
    backgroundColor: token.colorWarningBg,
    padding: '4px 8px',
    borderRadius: token.borderRadiusSM,
    marginBottom: 8,
  },

  sectionHeader: {
    fontSize: 11,
    fontWeight: 600,
    color: token.colorTextSecondary,
    paddingTop: 8,
    paddingBottom: 4,
    marginTop: 8,
    borderTop: `1px solid ${token.colorBorderSecondary}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionHint: {
    fontSize: 10,
    color: token.colorTextTertiary,
    fontWeight: 400,
  },

  userBtn: {
    cursor: 'pointer',
    color: token.colorPrimary,
    background: 'none',
    border: 'none',
    padding: 0,
    fontSize: 'inherit',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const CALLER_INTL_IDS: Record<string, string> = {
  'parse-detect-marker': 'monitor.llm.caller.parse-detect-marker',
  'parse-infer-meta': 'monitor.llm.caller.parse-infer-meta',
  'extract-characters': 'monitor.llm.caller.extract-characters',
  'extract-scenes': 'monitor.llm.caller.extract-scenes',
  'gen-character-prompts': 'monitor.llm.caller.gen-character-prompts',
  'storyboard-gen': 'monitor.llm.caller.storyboard-gen',
};

function useCallerLabel() {
  const intl = useIntl();
  return (caller: string) => {
    const id = CALLER_INTL_IDS[caller];
    if (id) return intl.formatMessage({ id });
    return caller || intl.formatMessage({ id: 'monitor.llm.unknown' });
  };
}

function formatElapsed(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}m${rem.toString().padStart(2, '0')}s`;
}

type TaskRowSharedProps = {
  userMap?: Record<string, AuthAPI.AdminUser>;
  onOpenUser?: (numericUserId: number) => void;
};
type TaskRowProps = TaskRowSharedProps &
  (
    | { kind: 'running'; task: LLMInFlightTask; leaseSeconds: number }
    | { kind: 'waiting'; task: LLMWaitingTask }
  );

const TaskRow: React.FC<TaskRowProps> = (props) => {
  const { styles } = useStyles();
  const intl = useIntl();
  const callerLabel = useCallerLabel();

  const task = props.task;
  let ms: number;
  if (props.kind === 'waiting') {
    ms = props.task.waited_ms;
  } else {
    ms = props.task.elapsed_ms;
  }

  const userChip: React.ReactNode = (() => {
    if (!task.user_name && !task.user_id) return null;
    const u = task.user_id ? props.userMap?.[task.user_id] : undefined;
    const display =
      u?.profile?.nickname ||
      u?.email ||
      task.user_name ||
      (task.user_id ? task.user_id.slice(-6) : '-');
    const clickable = !!(u && props.onOpenUser);
    return clickable ? (
      <button
        type="button"
        className={styles.userBtn}
        onClick={() => props.onOpenUser?.(u.userId)}
      >
        {display}
      </button>
    ) : (
      <span>{display}</span>
    );
  })();

  return (
    <div className={styles.taskRow}>
      <div className={styles.taskRowHead}>
        <Typography.Text strong style={{ fontSize: 12 }}>
          {callerLabel(task.caller)}
        </Typography.Text>
        <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
          {props.kind === 'waiting'
            ? intl.formatMessage(
                { id: 'monitor.llm.waitedPrefix' },
                { elapsed: formatElapsed(ms) },
              )
            : formatElapsed(ms)}
        </Tag>
      </div>
      <div className={styles.taskRowSub}>
        {userChip}
        {(task.project_title || task.project_id) && (
          <Tooltip
            title={
              task.project_title
                ? `${task.project_title} · project_id ${task.project_id || '-'}`
                : `project_id ${task.project_id}`
            }
          >
            <span>
              {task.project_title ||
                (task.project_id ? task.project_id.slice(-6) : '-')}
            </span>
          </Tooltip>
        )}
      </div>
      {task.note && <div className={styles.taskNote}>{task.note}</div>}
    </div>
  );
};

const LLMMonitor: React.FC = () => {
  const { styles } = useStyles();
  const intl = useIntl();
  const [data, setData] = React.useState<LLMInFlightResult | null>(null);

  const taskUserIds = React.useMemo(() => {
    const ids: string[] = [];
    for (const t of data?.tasks || []) if (t.user_id) ids.push(t.user_id);
    for (const t of data?.waiting || []) if (t.user_id) ids.push(t.user_id);
    return ids;
  }, [data]);
  const { userMap } = useUserMap(taskUserIds);
  const [openedUserId, setOpenedUserId] = React.useState<number | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const aliveRef = React.useRef(true);
  const backoffRef = React.useRef(POLL_INTERVAL_MS);
  const fetchOnceRef = React.useRef<() => Promise<void>>(async () => {});
  const wsConnectedRef = React.useRef(false);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    aliveRef.current = true;
    const runFetch = async () => {
      if (!aliveRef.current || document.hidden) return;
      try {
        const res = await getLLMInFlight();
        if (!aliveRef.current) return;
        if (res?.success && res.data) {
          setData(res.data);
          backoffRef.current = POLL_INTERVAL_MS;
        }
      } catch {

        backoffRef.current = Math.min(
          backoffRef.current * 2,
          POLL_BACKOFF_MAX_MS,
        );
      }
    };
    fetchOnceRef.current = runFetch;
    const tick = async () => {
      if (!aliveRef.current) return;

      if (document.hidden) {
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      await runFetch();
      if (aliveRef.current) {
        const next = wsConnectedRef.current
          ? WS_FALLBACK_MS
          : backoffRef.current;
        timerRef.current = setTimeout(tick, next);
      }
    };
    tick();
    const onVisible = () => {
      if (!document.hidden && aliveRef.current) {
        if (timerRef.current) clearTimeout(timerRef.current);
        backoffRef.current = POLL_INTERVAL_MS;
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      aliveRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const { connected: wsConnected } = usePlainRealtime({
    channel: 'llm',
    enabled: true,
    onPing: () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        void fetchOnceRef.current();
      }, 800);
    },
  });
  React.useEffect(() => {
    wsConnectedRef.current = wsConnected;
  }, [wsConnected]);

  const tasks = data?.tasks || [];

  const waiting = data?.waiting || [];
  const waitingCount = data?.waiting_count ?? waiting.length;
  const count = data?.count ?? tasks.length;
  const max = data?.max ?? 0;
  const leaseSeconds = data?.lease_seconds ?? 0;

  const limiterEnabled = max > 0;

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>
          {intl.formatMessage({ id: 'monitor.llm.popoverTitle' })}
        </span>
        {limiterEnabled ? (
          <span className={styles.panelMeta}>
            {count} / {max} ·{' '}
            {intl.formatMessage(
              { id: 'monitor.llm.leaseSuffix' },
              { seconds: leaseSeconds },
            )}
          </span>
        ) : (
          <span className={styles.panelMeta}>
            {intl.formatMessage({ id: 'monitor.llm.limiterDisabled' })}
          </span>
        )}
      </div>
      {data?.degraded && (
        <div className={styles.degradedBanner}>
          {intl.formatMessage(
            { id: 'monitor.llm.degraded' },
            {
              error:
                sanitizeError(data.error) ||
                intl.formatMessage({ id: 'monitor.llm.unknown' }),
            },
          )}
        </div>
      )}
      {tasks.length === 0 ? (
        <div className={styles.emptyWrap}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              limiterEnabled
                ? intl.formatMessage({ id: 'monitor.llm.emptyRunning' })
                : intl.formatMessage({ id: 'monitor.llm.emptyDisabled' })
            }
          />
        </div>
      ) : (
        tasks.map((t) => (
          <TaskRow
            key={t.holder}
            kind="running"
            task={t}
            leaseSeconds={leaseSeconds}
            userMap={userMap}
            onOpenUser={setOpenedUserId}
          />
        ))
      )}
      {}
      {waitingCount > 0 && (
        <>
          <div className={styles.sectionHeader}>
            <span>
              {intl.formatMessage(
                { id: 'monitor.llm.queueTitle' },
                { count: waitingCount },
              )}
            </span>
            <span className={styles.sectionHint}>
              {intl.formatMessage({ id: 'monitor.llm.queueHint' })}
            </span>
          </div>
          {waiting.map((w) => (
            <TaskRow
              key={w.holder}
              kind="waiting"
              task={w}
              userMap={userMap}
              onOpenUser={setOpenedUserId}
            />
          ))}
        </>
      )}

      {}
      <UserDetailModal
        userId={openedUserId}
        open={openedUserId !== null}
        onClose={() => setOpenedUserId(null)}
        includeTasksTab
      />
    </div>
  );
};

export default LLMMonitor;
